import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
} from '@mui/material';
import { Warning, CheckCircle, Error as ErrorIcon, Help } from '@mui/icons-material';

const ConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    title = "Are you sure?",
    message = "This action cannot be undone.",
    confirmText = "Confirm",
    cancelText = "Cancel",
    type = "warning", // 'warning', 'success', 'error', 'info'
    confirmColor = "primary", // 'primary', 'success', 'error'
    loading = false,
}) => {
    const getIcon = () => {
        switch (type) {
            case 'success':
                return <CheckCircle sx={{ fontSize: 64, color: '#4caf50' }} />;
            case 'error':
                return <ErrorIcon sx={{ fontSize: 64, color: '#ef5350' }} />;
            case 'info':
                return <Help sx={{ fontSize: 64, color: '#00a78e' }} />;
            default:
                return <Warning sx={{ fontSize: 64, color: '#ff9800' }} />;
        }
    };

    const getConfirmButtonStyle = () => {
        switch (confirmColor) {
            case 'success':
                return {
                    background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #45a049 0%, #4caf50 100%)',
                        boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                    },
                };
            case 'error':
                return {
                    background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
                    boxShadow: '0 4px 12px rgba(239, 83, 80, 0.3)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #e53935 0%, #ef5350 100%)',
                        boxShadow: '0 6px 16px rgba(239, 83, 80, 0.4)',
                    },
                };
            default:
                return {
                    background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                    boxShadow: '0 4px 12px rgba(0, 167, 142, 0.3)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #0f766e 0%, #00a78e 100%)',
                        boxShadow: '0 6px 16px rgba(0, 167, 142, 0.4)',
                    },
                };
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid rgba(0, 167, 142, 0.1)',
                    overflow: 'hidden',
                }
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(4px)',
                },
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '20px',
                    py: 2.5,
                    px: 3,
                    mb: 0,
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)',
                    },
                }}
            >
                {title}
            </DialogTitle>
            <DialogContent sx={{ p: 4, pt: 5, pb: 2, textAlign: 'center' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            mt: 1,
                            borderRadius: '50%',
                            background: type === 'warning' 
                                ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(245, 124, 0, 0.1) 100%)'
                                : type === 'success'
                                ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(56, 142, 60, 0.1) 100%)'
                                : type === 'error'
                                ? 'linear-gradient(135deg, rgba(239, 83, 80, 0.1) 0%, rgba(211, 47, 47, 0.1) 100%)'
                                : 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                            border: type === 'warning'
                                ? '2px solid rgba(255, 152, 0, 0.2)'
                                : type === 'success'
                                ? '2px solid rgba(76, 175, 80, 0.2)'
                                : type === 'error'
                                ? '2px solid rgba(239, 83, 80, 0.2)'
                                : '2px solid rgba(0, 167, 142, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {getIcon()}
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#1a1a1a',
                            fontSize: '16px',
                            fontWeight: 500,
                            lineHeight: 1.6,
                            maxWidth: '400px',
                        }}
                    >
                        {message}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 3,
                    pt: 1,
                    pb: 3,
                    gap: 2,
                    justifyContent: 'center',
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={loading}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        borderColor: 'rgba(0, 0, 0, 0.2)',
                        color: '#1a1a1a',
                        minWidth: '120px',
                        '&:hover': {
                            borderColor: 'rgba(0, 0, 0, 0.4)',
                            background: 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    {cancelText}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    disabled={loading}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        color: '#ffffff',
                        minWidth: '120px',
                        ...getConfirmButtonStyle(),
                        '&:disabled': {
                            background: 'rgba(0, 0, 0, 0.12)',
                            color: 'rgba(0, 0, 0, 0.26)',
                        },
                    }}
                >
                    {loading ? 'Processing...' : confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog;
